// src/components/ui/SafeMCCDialog.js
import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import logo from '../../assets/images/safemcc-logo-icon-v2.png';

/**
 * Reusable SafeMCC Dialog Component
 * 
 * Features:
 * - SafeMCC logo at the top
 * - Title with horizontal divider
 * - Customizable content
 * - Optional buttons
 * - Loading state support
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Function called when dialog should close
 * @param {string} title - Dialog title/header text
 * @param {React.ReactNode} children - Dialog content (message or custom JSX)
 * @param {Array} buttons - Array of button objects with { label, onClick, variant, color, disabled }
 * @param {boolean} showLoading - Whether to show loading spinner instead of content
 * @param {string} loadingMessage - Message to display during loading state
 * @param {string} maxWidth - Dialog max width ('xs', 'sm', 'md', 'lg', 'xl')
 * @param {boolean} disableEscapeKeyDown - Prevent closing dialog with Escape key
 * @param {boolean} disableBackdropClick - Prevent closing dialog by clicking backdrop
 * @param {string} titleIcon - Optional icon component to display next to title
 */
const SafeMCCDialog = ({
	open = false,
	onClose,
	title,
	children,
	buttons = [],
	showLoading = false,
	loadingMessage = 'Processing...',
	maxWidth = 'sm',
	disableEscapeKeyDown = false,
	disableBackdropClick = false,
	titleIcon: TitleIcon = null
}) => {
	// Handle backdrop click
	const handleClose = (event, reason) => {
		if (disableBackdropClick && reason === 'backdropClick') {
			return;
		}
		if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
			return;
		}
		if (onClose) {
			onClose();
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth={maxWidth}
			fullWidth
			disableEscapeKeyDown={disableEscapeKeyDown}
		>
			{/* Logo and Brand Section */}
			<div className="flex items-center gap-2 pt-6 pb-3 px-6">
				<h1 className="text-xl font-semibold text-gray-800">SafeMCC</h1>
				<img 
					src={logo} 
					alt="SafeMCC Logo" 
					className="h-8 w-auto"
				/>
			</div>

			{/* Title with Divider */}
			{title && (
				<>
					<DialogTitle 
						sx={{ 
							textAlign: 'left', 
							pb: 2,
							pt: 1,
							px: 3,
							fontSize: '1.25rem',
							fontWeight: 600,
							display: 'flex',
							alignItems: 'center',
							gap: 1
						}}
					>
						{TitleIcon && <TitleIcon className="text-gray-600" size={24} />}
						{title}
					</DialogTitle>
					<div className="border-t border-gray-300" />
				</>
			)}

			{/* Content */}
			<DialogContent sx={{ pt: 3, pb: 2, px: 4 }}>
				{showLoading ? (
					<div className="flex flex-col items-center justify-center py-8">
						<CircularProgress size={48} />
						<p className="mt-4 text-gray-700 font-medium">
							{loadingMessage}
						</p>
					</div>
				) : (
					<div className="text-gray-700">
						{children}
					</div>
				)}
			</DialogContent>

			{/* Buttons */}
			{buttons && buttons.length > 0 && !showLoading && (
				<DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
					{buttons.map((button, index) => (
						<Button
							key={index}
							onClick={button.onClick}
							variant={button.variant || 'text'}
							color={button.color || 'primary'}
							disabled={button.disabled || false}
							startIcon={button.startIcon}
							sx={{
								textTransform: 'none',
								fontWeight: 500,
								...(button.sx || {})
							}}
						>
							{button.label}
						</Button>
					))}
				</DialogActions>
			)}
		</Dialog>
	);
};

export default SafeMCCDialog;
